import React, { FC, useMemo } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { Photo } from '@withjoy/joykit/icons';
import { addRendition } from '@shared/utils/photoRendition';
import { useRouterHelper, useTranslation } from '@shared/core';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { WebsiteOverviewRoutes } from '@apps/admin/routes/WebsiteOverview/WebsiteOverview.routes';
import { MagicTokenPermissions } from '@graphql/generated';
import { CollectionItemProps, CollectionListsProps } from './CollectionLists.types';

const CollectionItem: FC<CollectionItemProps> = props => {
  const { collection, isSelected, onSelect } = props;

  const { t } = useTranslation('websiteEditor');
  const photoGalleryTransFn = t('customPage', 'photoGallery');

  const router = useRouterHelper();

  const mediaURL = useMemo(() => {
    const mediaPhoto = collection.thumbnails?.find(media => media.__typename === 'MediaPhoto');
    return mediaPhoto?.__typename === 'MediaPhoto' ? addRendition({ url: mediaPhoto.photo.url, renditionSize: 'medium' }) : '';
  }, [collection.thumbnails]);

  const collectionPrivacy = useMemo(
    () => (collection.magicTokenPermissions?.includes(MagicTokenPermissions.view) ? photoGalleryTransFn.public() : photoGalleryTransFn.hidden()),
    [collection.magicTokenPermissions, photoGalleryTransFn],
  );

  const origin = useMemo(() => withWindow(window => window.location.origin, ''), []);

  const handleAlbumEdit = useEventCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: string) => {
    e.stopPropagation();
    const path = router.buildPath(`${WebsiteOverviewRoutes.photos}/${id}`);
    const url = `${origin}${path}`;
    window.open(url, '_blank');
  });

  return (
    <Flex
      role="group"
      cursor="pointer"
      width="100%"
      flexDirection="column"
      borderRadius={pxToRem(12)}
      backgroundColor="white"
      onClick={() => onSelect(collection.id)}
      border="2px solid"
      borderColor={isSelected ? 'black' : 'transparent'}
    >
      <Flex
        width="100%"
        flexDirection="column"
        // inner border radius - 12px parent outer border radius - 2px parent border = 10px
        borderRadius={pxToRem(10)}
        overflow="hidden"
        boxShadow="0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)"
      >
        <Box
          width="100%"
          backgroundSize="cover"
          backgroundPosition="center"
          height={pxToRem(148)}
          backgroundColor="mono3"
          backgroundImage={`url("${mediaURL}")`}
          transition="opacity 0.2s"
          _groupHover={{
            opacity: 0.8,
          }}
        />
        <Flex
          padding={4}
          flexDirection="column"
          gap={3}
        >
          <Flex flexDirection="column">
            <TextV2
              typographyVariant="label1"
              color="mono14"
            >
              {collectionPrivacy}
            </TextV2>
            <TextV2
              typographyVariant="hed1"
              color="mono14"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {collection.name}
            </TextV2>
          </Flex>
          <Flex
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
            gap={3}
          >
            <Flex
              flexDirection="row"
              alignItems="center"
              gap={3}
            >
              <Photo size={16} />
              <TextV2
                typographyVariant="label1"
                color="mono12"
              >
                {`${collection.mediaCount} ${collection.mediaCount === 1 ? photoGalleryTransFn.photo() : photoGalleryTransFn.photos()}`}
              </TextV2>
            </Flex>
            <ButtonV2
              typographyVariant="label1"
              color="mono12"
              variant="link"
              textDecoration="underline"
              onClick={e => handleAlbumEdit(e, collection.id)}
            >
              {photoGalleryTransFn.editAlbum()}
            </ButtonV2>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};

export const CollectionLists: React.FC<CollectionListsProps> = props => {
  const { mediaCollections, handleSelectCollection, selectedCollectionId } = props;

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(2, minmax(0, 1fr))"
      width="100%"
      gap={5}
    >
      {mediaCollections?.map(colection => (
        <CollectionItem
          key={colection.id}
          collection={colection}
          isSelected={selectedCollectionId === colection.id}
          onSelect={handleSelectCollection}
        />
      ))}
    </Box>
  );
};

CollectionLists.displayName = 'CollectionLists';
